import React, { useCallback, useState } from 'react';
import { Col, Row, Typography } from 'antd';
import { Colors } from '@common/tokens';
import { FontSizes } from 'works/legacy/lib/style-helper';
import { useNavigate, useParams } from '@common/hooks';
import {
  AlignedInfo,
  History,
  Icon,
  IconOfKind,
  StatePill,
  Tabs,
} from '@common/components';
import {
  WorkerAssignmentsTable,
  WorkerDetailUnitsTable,
  WorkerShiftsTable,
  WorksShiftMatchesTable,
} from '@scheduling/components';
import { WorkerActiveDropdown } from './_worker-active-dropdown';
import { NotificationPreferencesModal } from './_notification-preferences-modal';
import { WorkerDetailQueryCgResponse } from '@common/graphql/schema';
import { clsx } from 'clsx';
import { useCurrentMembership } from '@identity/hooks';

const { Title } = Typography;

type Props = {
  worker: WorkerDetailQueryCgResponse['worker'];
  setShowDrawer: (toggle: boolean) => void;
  workerIntegrationEnabled?: boolean;
  workerFound?: boolean;
  mappedHomeUnit?: boolean;
};

export function WorkerDetails({
  worker,
  setShowDrawer,
  workerIntegrationEnabled,
  workerFound,
  mappedHomeUnit,
}: Props) {
  const currentMembership = useCurrentMembership();
  const { tabKey } = useParams<{ tabKey: string }>();

  const [
    showNotificationPreferencesModal,
    setShowNotificationPreferencesModal,
  ] = useState(false);

  const navigate = useNavigate();

  const handleTabChange = useCallback(
    (key: string) => {
      navigate(`/works/team/on_demand/${worker.id}/${key}`);
    },
    [navigate, worker]
  );

  const homeUnitTitle = (() => {
    if (worker.homeUnit) {
      return worker?.homeUnit?.name;
    }

    if (workerIntegrationEnabled && workerFound && !mappedHomeUnit) {
      return 'Unit/role not in Works';
    }

    return 'Unassigned';
  })();

  const workforceGroupName =
    (worker?.workforceGroup?.active && worker?.workforceGroup?.name) || ' - ';

  const fteLabel =
    worker.ftePercentage !== null && worker.ftePercentage !== undefined
      ? `${worker.ftePercentage}%`
      : '';

  return (
    <div className="worker-details">
      <NotificationPreferencesModal
        open={showNotificationPreferencesModal}
        setOpen={setShowNotificationPreferencesModal}
        workerId={worker.id}
      />
      <Row justify="space-between">
        <Col>
          <Title level={3} className="worker-details__title sentry-mask">
            {worker.worksUser?.name}
          </Title>
          <StatePill
            color={worker.active ? 'grass' : 'coral'}
            fontSize={FontSizes.small}
          >
            {worker.active ? 'Active' : 'Inactive'}
          </StatePill>
        </Col>
        {(currentMembership.canEditWorkers || workerIntegrationEnabled) && (
          <Col span={4} className="worker-actions-button">
            <WorkerActiveDropdown
              worker={worker}
              setShowDrawer={setShowDrawer}
              canEditWorkers={currentMembership.canEditWorkers}
              integrationEnabled={workerIntegrationEnabled}
            />
          </Col>
        )}
      </Row>
      <AlignedInfo
        labelOrientation="none"
        fullWidth={false}
        content={[
          [
            <WorkerInfoItem
              key="employeeId"
              icon={{ icon: 'idCard', kind: 'light' }}
              tooltip="Social Security Number"
              label={worker.employeeId}
            />,
            <WorkerInfoItem
              key="role"
              icon={{ icon: 'userMd', kind: 'light' }}
              tooltip="Role"
              label={worker.role?.abbreviation}
            />,
            <WorkerInfoItem
              key="ftePercentage"
              icon={{ icon: 'userClock', kind: 'light' }}
              tooltip="FTE %"
              label={fteLabel}
            />,
          ],
          [
            <WorkerInfoItem
              key="user"
              icon={{ icon: 'envelope', kind: 'light' }}
              tooltip="Email"
              label={worker.worksUser.email}
              className="sentry-mask"
            />,
            <WorkerInfoItem
              key="workforceGroup"
              icon={{ icon: 'users', kind: 'light' }}
              tooltip="Group"
              label={workforceGroupName}
            />,
          ],
          [
            <WorkerInfoItem
              key="phone"
              icon={{ icon: 'mobile', kind: 'light' }}
              tooltip="Mobile Number"
              label={worker.worksUser.phone}
              rightComponent={
                <>
                  <div style={{ marginRight: 6, marginLeft: 6 }}>-</div>
                  <div
                    className="button-notification"
                    onClick={() => setShowNotificationPreferencesModal(true)}
                  >
                    Notification Preferences
                  </div>
                </>
              }
              className="sentry-mask"
            />,
            <WorkerInfoItem
              key="homeUnitTitle"
              icon={{ icon: 'houseChimneyMedical', kind: 'regular' }}
              tooltip="Home Unit"
              label={homeUnitTitle}
            />,
          ],
        ]}
      />
      <div style={{ marginBottom: 24 }}></div>
      <Tabs
        onChange={handleTabChange}
        activeKey={tabKey}
        style={{ marginTop: 8 }}
      >
        {!worker.isInternal && (
          <Tabs.Pane tab="Assignments" key="assignments">
            <WorkerAssignmentsTable workerId={worker.id} />
          </Tabs.Pane>
        )}
        <Tabs.Pane tab="Units" key="units">
          <div style={{ marginTop: 10 }}>
            <WorkerDetailUnitsTable
              allowLists={worker?.allowLists || []}
              allowedUnits={worker?.allowedUnits || []}
              homeUnitId={worker.homeUnit?.id}
            />
          </div>
        </Tabs.Pane>
        <Tabs.Pane tab="Matches" key="matches">
          <WorksShiftMatchesTable workerId={worker.id} />
        </Tabs.Pane>
        <Tabs.Pane tab="Shifts" key="on_demand">
          <WorkerShiftsTable worker={worker} />
        </Tabs.Pane>
        <Tabs.Pane tab="History" key="history">
          <div style={{ marginTop: 24 }}>
            <History data={worker.history || []} />
          </div>
        </Tabs.Pane>
      </Tabs>
      <style jsx>{`
        .worker-details :global(.FilteredTable) {
          margin-top: 9px;
        }
        .worker-details :global(.worker-actions-button) {
          display: flex;
          justify-content: flex-end;
        }
        .worker-details :global(.worker-details__title) {
          display: inline-block;
          vertical-align: sub;
          margin-right: 16px;
        }
        .worker-details :global(.wk-aligned-info__col) {
          padding-bottom: 8px;
        }
        .worker-details :global(.button-notification) {
          color: ${Colors.elm};
          margin: 0;
          cursor: pointer;
          display: flex;
          align-items: center;
        }
      `}</style>
    </div>
  );
}

function WorkerInfoItem({
  icon,
  tooltip,
  label,
  rightComponent,
  className,
}: {
  icon: IconOfKind;
  tooltip: string;
  label?: string | null;
  rightComponent?: React.ReactNode;
  className?: string;
}) {
  return (
    <Row align="middle" className={clsx('worker-info', className)}>
      <span className="worker-info-item__icon">
        <Icon {...icon} size="sm" tooltipText={tooltip} />
      </span>
      <span className="worker-info-item__label">{label || 'Not provided'}</span>
      {rightComponent}
      <style jsx>{`
        .worker-info-item__icon {
          width: 16px;
          display: inline-block;
          text-align: center;
        }
        .worker-info-item__label {
          margin-left: 8px;
        }
        :global(.worker-info) {
          margin-right: 122px;
        }
      `}</style>
    </Row>
  );
}
